'use client'

import React from 'react'
import classNames from 'classnames'
import Icon, { IconName } from '../icon'
import { AccordionWrapper, useAccordionWrapper } from './accordion-wrapper'
import { AccordionButtonProps } from './types'
import ConditionalWrapper from '../utils/conditional-wrapper'
import Link from 'next/link'

export interface AccordionProps extends AccordionButtonProps {
  /** Id attribute used on the accordion button */
  buttonId?: string | null
  /** Icon show to the left on the accordion button */
  icon?: IconName
  iconComponent?: React.ReactNode
  /**
   * Hide the left border on the accordion button that is visible when the
   * accordion is active (`.no-background`)
   */
  noBackground?: boolean
  href?: string
  handleAccordionIsOpen?: (isOpen: boolean) => void
  iconSize?: string
}

export const AccordionDefault = (props: AccordionProps) => {
  const {
    title,
    buttonId,
    buttonClassName,
    noBackground,
    extOpen,
    icon,
    children,
    href,
    iconSize,
    handleAccordionIsOpen,
    iconComponent,
  } = props

  const accordionWrapperProps = useAccordionWrapper({
    extOpen,
    handleAccordionIsOpen,
  })
  const { buttonRef, isOpen, setOpen, setButtonClick, panelId } = accordionWrapperProps

  return (
    <>
      <AccordionWrapper
        {...props}
        {...accordionWrapperProps}
        button={
          <ConditionalWrapper
            condition={!!href}
            wrapper={children => (
              <Link shallow legacyBehavior scroll={false} prefetch={false} href={href ?? ''}>
                {children}
              </Link>
            )}>
            <button
              {...(buttonId ? { id: buttonId } : {})}
              type="button"
              ref={buttonRef}
              onClick={() => {
                setOpen(!isOpen)
                setButtonClick(true)
              }}
              className={classNames(buttonClassName, 'c-accordion__btn', {
                'no-background': noBackground,
              })}
              aria-controls={panelId}
              aria-expanded={isOpen}>
              <>
                {icon && (
                  <Icon
                    className={classNames(iconSize, 'd-flex align-items-center', {
                      'i-16': !iconSize,
                    })}
                    name={icon}
                  />
                )}
                {iconComponent ? iconComponent : null}
                {title}
              </>
            </button>
          </ConditionalWrapper>
        }>
        {children}
      </AccordionWrapper>
    </>
  )
}
